import {
  IResponseError,
  ISwrConfiguration,
  ISwrResponse,
  useFetch,
  useOnScreen,
} from 'lib/hooks';
import { IFetchResponse } from 'lib/requests/fetch';
import { ISearchResult } from 'lib/types';
import { UserProfileRole } from 'lib/types/user';
import _ from 'lodash';
import { RefCallback, useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  getPopulationUserIds,
  IActiveLearnerSearchPayload,
  searchLearners,
} from '../api/search';
import { IAutomatedSchedulerPopulationPayload } from '../training-campaigns/scheduler/api';
import { IDashboardLearnerInfo } from '../types';

export interface IUseInfiniteSearchResult<T> {
  data: ISearchResult<T>[];
  error: any;
  isLoading: boolean;
  isValidating: boolean;
  scrollRef: RefCallback<HTMLDivElement>;
}

export type UseInfiniteSearchHook<P, T> = (
  payload: Omit<P, 'limit' | 'offset'>,
  options?: ISwrConfiguration
) => IUseInfiniteSearchResult<T>;

const handleResult = <T>(result: IFetchResponse<T>) => {
  if (result.error) {
    const e: IResponseError = new Error();
    e.data = result.data;
    e.message = result.message;
    throw e;
  } else {
    return result.data;
  }
};

export interface IPopulationCountQueryParams {
  departments?: string[];
  enrolledInAutomatedSchedulerCourseId?: number;
  locations?: string[];
  roles?: UserProfileRole[];
}

export const usePopulationCount = (
  queryParams?: IPopulationCountQueryParams,
  options?: ISwrConfiguration
) =>
  useFetch<number>(['/admin/dashboard/population-count', queryParams], options);

export const useInProgressAssignmentPopulationCount = (assignmentKey: string) =>
  useFetch<number>(
    `/admin/dashboard/assignments/${assignmentKey}/in-progress-assignment-population-count`
  );

/**
 * Mechanism for fetching paginated search results for a set of user IDs. Since
 * this is a POST request, we can't use our standard `useInfiniteScroll` hook.
 */
export const useInfiniteSearchUsers = <P, T>(
  endpoint: string,
  payload: Omit<P, 'limit' | 'offset'>,
  options?: ISwrConfiguration
): IUseInfiniteSearchResult<T> => {
  const [scrollRef, setScrollRef] = useState<HTMLDivElement>();

  const { shouldFetch, ...rest } = options ?? {};
  const { data, error, isLoading, isValidating, size, setSize } =
    useSWRInfinite<ISearchResult<T>>(
      (pageIndex) => {
        if (!(shouldFetch ?? true)) {
          return null;
        }
        return [
          endpoint,
          _.extend({ limit: 25, offset: pageIndex * 25 }, payload),
        ];
      },
      async ([_, p]: [string, P]) => {
        return handleResult(await searchLearners<P, T>(endpoint, p));
      },
      {
        revalidateFirstPage: false,
        initialSize: 1,
        ...rest,
      }
    );

  const hasReachedEnd = !_.isEmpty(data)
    ? data[data.length - 1].results.length < 25
    : false;
  const shouldLoadMore =
    useOnScreen(scrollRef) && _.first(data)?.total > 25 && !isValidating;

  useEffect(() => {
    if (!hasReachedEnd && shouldLoadMore) {
      setSize(size + 1);
    }
  }, [hasReachedEnd, shouldLoadMore]);

  return {
    data,
    error,
    isLoading,
    isValidating,
    scrollRef: useCallback((node: HTMLDivElement) => setScrollRef(node), []),
  };
};

export const useInfiniteSearchActiveTeamMembers = _.partial(
  useInfiniteSearchUsers<IActiveLearnerSearchPayload, IDashboardLearnerInfo>,
  '/admin/dashboard/search-active-learners'
);

export const useInfiniteSearchActiveTeamMembersWithEmail = (
  payload: Omit<IActiveLearnerSearchPayload, 'limit' | 'offset'>,
  options?: ISwrConfiguration
) =>
  useInfiniteSearchActiveTeamMembers(
    {
      ...payload,
      hasEmail: true,
    },
    options
  );

export const getUseInfiniteSearchEnrolledLearners = (
  automatedSchedulerConfigId: number
) =>
  _.partial(
    useInfiniteSearchUsers<IActiveLearnerSearchPayload, IDashboardLearnerInfo>,
    `/admin/dashboard/automated-scheduler/${automatedSchedulerConfigId}/search-enrolled-learners`
  );

export const usePopulationUserIds = (
  payload: IAutomatedSchedulerPopulationPayload,
  options: ISwrConfiguration = {}
): ISwrResponse<number[]> => {
  const { shouldFetch, ...rest } = options;
  return useSWR(
    (shouldFetch ?? true) && [
      '/admin/dashboard/scheduling/get-population-member-ids',
      payload,
    ],
    async ([_, p]: [string, IAutomatedSchedulerPopulationPayload]) => {
      return handleResult(await getPopulationUserIds(p));
    },
    { keepPreviousData: true, ...rest }
  );
};
